import * as yup from 'yup';
import { useCallback, useMemo } from 'react';
import { FieldsType } from '../../constants/FieldsType';
import { Field } from '../../interfaces/Field';
import { requiredFieldMessage } from '../../constants/errorMessages';

export const useSchema = (metadataFields: Field[]) => {
  const numberStringTransform = useCallback(
    (value: any, originalValue: any) =>
      originalValue?.toString().trim() === '' ? null : value,
    []
  );

  const getArrayValidator = useCallback(
    (isRequired?: boolean) => {
      return isRequired
        ? yup
            .array()
            .min(1, requiredFieldMessage)
            .required(requiredFieldMessage)
        : yup.array();
    },

    /* eslint-disable react-hooks/exhaustive-deps */
    [metadataFields]
  );

  const getStringValidator = (isRequired?: boolean) =>
    isRequired ? yup.string().required(requiredFieldMessage) : yup.string();

  const getValidatorForType = (entity: Field) => {
    switch (entity.type) {
      case FieldsType.string:
        return entity.isArray
          ? getArrayValidator(entity.required)
          : getStringValidator(entity.required);
      case FieldsType.number:
        return entity.required
          ? yup.number().required(requiredFieldMessage)
          : yup.number().transform(numberStringTransform).nullable();
      case FieldsType.bool:
        return entity.required
          ? yup.boolean().required(requiredFieldMessage)
          : yup.boolean();
      case FieldsType.dateTime:
        return entity.required
          ? yup.date().required(requiredFieldMessage)
          : yup.array();
      case FieldsType.xdm:
      case FieldsType.xdmContext:
      case FieldsType.multiSelect:
        return getArrayValidator(entity.required);
      default:
        return null;
    }
  };

  const schema = useMemo(() => {
    const fields = metadataFields.reduce((fieldsAccumulator, entity) => {
      const validation = getValidatorForType(entity);
      if (validation) {
        fieldsAccumulator[entity.name] = validation;
      }
      return fieldsAccumulator;
    }, {});

    return yup.object().shape(fields);
  }, [metadataFields, numberStringTransform, getValidatorForType]);

  return { schema };
};
